<template>
    <div>
        <v-row>
            <v-col>
                <h4 class="text-h4">Trial Balance</h4>
                <p class="text-subtitle-1">Trial Balance Report</p>
            </v-col>
        </v-row>
        <v-divider></v-divider>
        <v-row class="my-2">
            <v-col cols="2">
                <v-autocomplete
                    label="Year"
                    v-model="query.year_id"
                    outlined
                    hide-details
                    dense
                    background-color="white"
                    :items="years"
                    item-text="name"
                    item-value="id"
                    @update:search-input="fetchYearsAsync"
                    @change="clearPeriod"
                    clearable
                    >
                </v-autocomplete>
            </v-col>
            <v-col cols="2">
                <v-autocomplete
                    label="Period"
                    v-model="query.period_id"
                    :items="periods"
                    item-text="name"
                    item-value="id"
                    outlined
                    hide-details
                    dense
                    background-color="white"
                    clearable
                    >
                </v-autocomplete>
            </v-col>
            <v-col cols="1">
                <v-btn block depressed color="primary" @click="fetchTrialBalance" :loading="loading.data">
                    <v-icon left>mdi-magnify</v-icon>
                    search
                </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="1" v-if="trialBalance.length > 0 && this.hasPermission(this.perm.trial_balance.exportCSV)">
                <v-btn icon color="primary" @click="exportCSV">
                    <v-icon>mdi-microsoft-excel</v-icon>
                </v-btn>
            </v-col>
        </v-row>
        <div v-if="trialBalance.length > 0">
            <div class="d-flex justify-end">
                <div class="me-2">
                    <tr>
                        <th colspan="2" class="tr_title">Opening Balance</th>
                        <th colspan="2" class="tr_title">Period Movement</th>
                        <th colspan="2" class="tr_title">Closing Balance</th>
                    </tr>
                    <tr>
                        <td class="tr_title">Debit</td>
                        <td class="tr_title">Credit</td>
                        <td class="tr_title">Debit</td>
                        <td class="tr_title">Credit</td>
                        <td class="tr_title">Debit</td>
                        <td class="tr_title">Credit</td>
                    </tr>
                </div>
            </div>
            <v-treeview
                item-key="account_id"
                item-text="display_name"
                :open="[]"
                dense
                activatable
                open-on-click
                :items="trialBalance">
                <template v-slot:append="{ item }">
                    <tr>
                        <td class="tr_balance">{{formatNumber(item.opening_debit, decimal)}}</td>
                        <td class="tr_balance">{{formatNumber(item.opening_credit, decimal)}}</td>
                        <td class="tr_balance">{{formatNumber(item.movement_debit, decimal)}}</td>
                        <td class="tr_balance">{{formatNumber(item.movement_credit, decimal)}}</td>
                        <td class="tr_balance">{{formatNumber(item.closing_debit, decimal)}}</td>
                        <td class="tr_balance">{{formatNumber(item.closing_credit, decimal)}}</td>
                    </tr>
                </template>
            </v-treeview>
            <div class="d-flex justify-end">
                <div class="me-2 mt-2">
                    <tr class="grey white--text">
                        <td class="tr_title font-weight-bold py-2">{{formatNumber(getTotal(trialBalance, "opening_debit"), decimal)}}</td>
                        <td class="tr_title font-weight-bold py-2">{{formatNumber(getTotal(trialBalance, "opening_credit"), decimal)}}</td>
                        <td class="tr_title font-weight-bold py-2">{{formatNumber(getTotal(trialBalance, "movement_debit"), decimal)}}</td>
                        <td class="tr_title font-weight-bold py-2">{{formatNumber(getTotal(trialBalance, "movement_credit"), decimal)}}</td>
                        <td class="tr_title font-weight-bold py-2">{{formatNumber(getTotal(trialBalance, "closing_debit"), decimal)}}</td>
                        <td class="tr_title font-weight-bold py-2">{{formatNumber(getTotal(trialBalance, "closing_credit"), decimal)}}</td>
                    </tr>
                </div>
            </div>
        </div>
        <div v-else-if="errors.length == 0" class="d-flex justify-center pa-4">
            <v-icon>mdi-alert-circle-outline</v-icon>
            <span class="ms-2">No Data, Please Search ...</span>
        </div>
        <div v-else class="d-flex justify-center pa-4">
            <v-icon class="error--text">mdi-alert-circle-outline</v-icon>
            <span class="ms-2 error--text">{{errors[0]}}</span>
        </div>
        <v-overlay v-model="loading.data">
            <v-sheet color="white" class="pa-4" width="300" elevation="1" rounded>
                <v-progress-linear indeterminate color="primary"/>
                <div class="primary--text text-center mt-2">Please Wait ...</div>
            </v-sheet>
        </v-overlay>
    </div>
</template>

<script>
// import PDatePicker from './../../components/common/PDatePicker';
import { getTrialBalance } from './../../services/ledger/reports';
import { formatNumber } from 'accounting';
import { getTotal } from './../../utils/accounting';
import yearService from './../../services/ledger/year';
import permissions from '../../data/permissions';
import { mapGetters } from 'vuex';

export default {
    name: "TrailBalance",
    components: {
        // PDatePicker
    },
    data() {
        return {
            loading: {
                data: false
            },
            query: {
                year_id: null,
                period_id: null
            },
            trialBalance: [],
            decimal: 2,
            errors: [],
            years: [],
            perm: permissions
        }
    },
    computed: {
        ...mapGetters({
             hasPermission: "authentication/hasPermission"
        }),
        year() {
            return this.years.find(year => year.id == this.query.year_id);
        },
        periods() {
            if(!this.year) return [];
            return this.year.periods;
        }
    },
    methods: {
        getTotal,
        formatNumber,
        async fetchTrialBalance() {
            try {
                this.trialBalance = [];
                this.errors = [];
                this.loading.data = true;
                let resp = await getTrialBalance(this.query);
                this.trialBalance = resp.data;
                this.loading.data = false;
            } catch (error) {
                this.loading.data = false;
                let message = error.response ? error.response.data.message : error;
                this.errors.push(message);
            }
        },
        fetchYearsAsync(query) {
            yearService.search(query).then(resp => {
                this.years = resp.data;
            }).catch(error => {
                let message = error.response ? error.response.data.message : error;
                console.log(message)
            });
        },
        clearPeriod() {
            this.query.period_id = null;
        },
        async exportCSV() {
            let response = await getTrialBalance(this.query, {"Accept": "application/csv"});
            const blob = new Blob([response.data], { type: 'application/csv' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = "trial-balance.csv"
            link.click()
            URL.revokeObjectURL(link.href)
        }
    },
    created() {

    }
}
</script>

<style scoped>
    .tr_balance {
        width: 140px;
        text-align: left;
        font-size: 0.8em;
    }

    .tr_title {
        width: 140px;
        text-align: left;
        font-size: 13px;
    }
</style>